// File: frontend/src/pages/LandingPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    styled,
    alpha,
    Fade,
    Stack,
    Chip,
    Divider,
    Link
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EmailIcon from '@mui/icons-material/Email';
import LoginIcon from '@mui/icons-material/Login';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ShieldIcon from '@mui/icons-material/Shield';
import ChatIcon from '@mui/icons-material/Chat';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StorageIcon from '@mui/icons-material/Storage';
import XIcon from '@mui/icons-material/X';
import ScienceIcon from '@mui/icons-material/Science';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { publications } from '../data/publications';
import SEOHead from '../components/SEOHead';
import { cyber } from '../theme';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${cyber.bgDefault} 0%, ${cyber.bgSidebar} 50%, ${cyber.bgPaper} 100%)`,
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: `radial-gradient(circle, ${alpha(cyber.primary, 0.15)} 0%, transparent 70%)`,
        animation: 'pulse 15s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)' },
        '50%': { transform: 'scale(1.1)' },
    }
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    height: '100%',
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: cyber.glow,
        borderColor: alpha(theme.palette.primary.main, 0.3),
    }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    borderRadius: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
    marginBottom: theme.spacing(2),
}));

const CTAButton = styled(Button)(({ theme }) => ({
    background: cyber.gradient,
    color: 'white',
    fontWeight: 'bold',
    padding: '16px 48px',
    fontSize: '1.1rem',
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    boxShadow: `0 8px 24px ${alpha(cyber.primary, 0.5)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        background: cyber.gradientHover,
        boxShadow: cyber.glow,
        transform: 'translateY(-3px)',
    }
}));

const StatsBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    background: alpha(theme.palette.background.paper, 0.05),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    textAlign: 'center',
}));

const Section = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8, 0),
}));

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const { instance } = useMsal();

    const handleLogin = () => {
        instance.loginPopup(loginRequest)
            .then(response => {
                instance.setActiveAccount(response.account);
                navigate('/');
            })
            .catch(e => console.error(e));
    };

    const coreFeatures = [
        {
            icon: <CloudUploadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: 'Automated Ingestion',
            description: 'Continuous monitoring and processing of threat intelligence from curated OSINT sources. Support for web articles. PDFs and structured reports coming soon.'
        },
        {
            icon: <PsychologyIcon sx={{ fontSize: 32, color: cyber.accent.cyan }} />,
            title: 'AI-Powered Analysis',
            description: 'Advanced LLM processing generates technical summaries, extracts IOCs, generates STIX 2.1 bundles, maps TTPs to MITRE ATT&CK, creates visual mindmaps, and determines attack execution sequences.'
        },
        {
            icon: <TrendingUpIcon sx={{ fontSize: 32, color: cyber.accent.green }} />,
            title: 'Weekly Intelligence Briefing',
            description: 'Multi-agent AI system analyzes weekly trends, identifies top TTPs and targeted sectors, and provides deep-dive analysis on significant threats.'
        },
        {
            icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: cyber.accent.coral }} />,
            title: 'STIX 2.1 Export',
            description: 'Download standardized STIX bundles for seamless integration with SIEMs, SOARs, and Threat Intelligence Platforms (TIPs).'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
            title: 'IOC Search Engine',
            description: 'Fast lookup across all processed reports for IP addresses, domains, file hashes, and other indicators of compromise.'
        },
        {
            icon: <CloudUploadIcon sx={{ fontSize: 32, color: cyber.accent.cyan }} />,
            title: 'Article Submissions',
            description: 'Submit your own threat intelligence articles for AI-powered analysis. Community-driven intelligence enrichment.'
        },
        {
            icon: <AssessmentIcon sx={{ fontSize: 32, color: 'info.main' }} />,
            title: 'Platform Statistics',
            description: 'Real-time analytics dashboard showing processing metrics, top sources, threat trends, IOC and CVE distributions.'
        },
        {
            icon: <StorageIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
            title: 'MCP Server (Coming Soon)',
            description: 'Model Context Protocol server integration for enhanced LLM interactions and context management across the platform.'
        },
        {
            icon: <AccountTreeIcon sx={{ fontSize: 32, color: 'success.main' }} />,
            title: 'Knowledge Graph (Coming Soon)',
            description: 'LLM-generated graph visualization of relationships between threat actors, malware families, TTPs, and infrastructure.'
        },
        {
            icon: <DescriptionIcon sx={{ fontSize: 32, color: 'error.main' }} />,
            title: 'PDF Report Generation (Coming Soon)',
            description: 'Automated Prioritization, Detection, and Response PDF report generation based on analyzed threat intelligence writeups.'
        },
        {
            icon: <ChatIcon sx={{ fontSize: 32, color: cyber.accent.coral }} />,
            title: 'Knowledge Base GenAI Chat (Coming Soon)',
            description: 'Interactive AI assistant trained on your processed intelligence for natural language queries and threat research.'
        }
    ];

    const useCases = [
        {
            icon: <ManageSearchIcon sx={{ fontSize: 28 }} />,
            role: 'Threat Analysts',
            benefit: 'Reduce analysis time from hours to minutes with AI-generated summaries and automated IOC extraction'
        },
        {
            icon: <ShieldIcon sx={{ fontSize: 28 }} />,
            role: 'SOC Teams',
            benefit: 'Rapidly deploy detection rules and threat hunting queries with ready-to-use IOCs and TTPs'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 28 }} />,
            role: 'Incident Responders',
            benefit: 'Gain immediate context on adversary behavior and tactics during active investigations'
        },
        {
            icon: <DescriptionIcon sx={{ fontSize: 28 }} />,
            role: 'Security Leadership',
            benefit: 'Stay informed with automated weekly briefings and high-level threat landscape summaries'
        }
    ];

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            <SEOHead
                title="TI Mindmap HUB - AI-Powered Cyber Threat Intelligence Research Platform"
                description="An experimental research platform exploring Generative AI applications in Cyber Threat Intelligence. Transform unstructured threat reports into actionable intelligence with automated IOC extraction, MITRE ATT&CK mapping, and STIX 2.1 bundles."
                canonical="/"
            />
            {/* Hero Section */}
            <HeroSection>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Fade in timeout={800}>
                        <Box sx={{ textAlign: 'center', color: 'white', py: 8 }}>
                            {/* Logo/Brand */}
                            <Box
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    background: cyber.gradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto',
                                    marginBottom: 4,
                                    boxShadow: cyber.glowStrong,
                                    animation: 'float 3s ease-in-out infinite',
                                    '@keyframes float': {
                                        '0%, 100%': { transform: 'translateY(0)' },
                                        '50%': { transform: 'translateY(-15px)' },
                                    }
                                }}
                            >
                                <PsychologyIcon sx={{ fontSize: 64 }} />
                            </Box>

                            <Typography variant="h2" fontWeight="bold" gutterBottom>
                                TI Mindmap HUB
                            </Typography>
                            
                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                                <Chip
                                    icon={<ScienceIcon sx={{ color: `${cyber.accent.cyan} !important` }} />}
                                    label="RESEARCH PROJECT"
                                    sx={{
                                        bgcolor: alpha(cyber.accent.cyan, 0.12),
                                        color: cyber.accent.cyan,
                                        fontWeight: 'bold',
                                        border: `1px solid ${alpha(cyber.accent.cyan, 0.45)}`
                                    }}
                                />
                                <Chip
                                    label="BETA"
                                    sx={{
                                        bgcolor: alpha(cyber.primary, 0.15),
                                        color: cyber.primaryLight,
                                        fontWeight: 'bold',
                                        border: `1px solid ${alpha(cyber.primary, 0.4)}`
                                    }}
                                />
                            </Stack>

                            {/* Peer-reviewed publication badge */}
                            <Box sx={{ mb: 3 }}>
                                <Chip
                                    icon={<MenuBookIcon sx={{ color: `${cyber.accent.green} !important` }} />}
                                    label={`Research published in ${publications[0].venue} (${publications[0].year})`}
                                    component="a"
                                    href="/research#publications"
                                    clickable
                                    sx={{
                                        bgcolor: alpha(cyber.accent.green, 0.1),
                                        color: cyber.accent.green,
                                        fontWeight: 600,
                                        border: `1px solid ${alpha(cyber.accent.green, 0.4)}`,
                                        maxWidth: '100%',
                                        '& .MuiChip-label': { whiteSpace: 'normal' },
                                        height: 'auto',
                                        py: 0.5,
                                    }}
                                />
                            </Box>

                            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
                                An Experimental Research Platform Exploring Generative AI in Cyber Threat Intelligence
                            </Typography>

                            <Typography variant="body1" sx={{ mb: 5, opacity: 0.8, maxWidth: '700px', mx: 'auto', lineHeight: 1.8 }}>
                                Automate OSINT processing with advanced Generative AI and Agents. Transform unstructured threat reports 
                                into actionable intelligence with automated IOC extraction, MITRE ATT&CK mapping, STIX bundles and visual analysis.
                            </Typography>

                            {/* CTA Buttons */}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 6 }}>
                                <CTAButton
                                    size="large"
                                    onClick={handleLogin}
                                    startIcon={<LoginIcon />}
                                >
                                    Access Platform
                                </CTAButton>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    onClick={() => navigate('/reports')}
                                    startIcon={<ArticleIcon />}
                                    sx={{
                                        color: 'white',
                                        borderColor: 'white',
                                        padding: '16px 48px',
                                        fontSize: '1.1rem',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            borderColor: 'white',
                                            bgcolor: alpha('#fff', 0.1)
                                        }
                                    }}
                                >
                                    Browse Reports
                                </Button>
                                <Button
                                    size="large"
                                    variant="text"
                                    href="#features"
                                    sx={{
                                        color: 'white',
                                        padding: '16px 32px',
                                        fontSize: '1.1rem',
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            bgcolor: alpha('#fff', 0.1)
                                        }
                                    }}
                                >
                                    Learn More
                                </Button>
                            </Stack>

                            {/* Key Stats */}
                            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, maxWidth: '900px', mx: 'auto' }}>
                                <StatsBox>
                                    <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1, color: cyber.accent.coral }} />
                                    <Typography variant="h4" fontWeight="bold">100%</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>GenAI-Automated</Typography>
                                </StatsBox>
                                <StatsBox>
                                    <SpeedIcon sx={{ fontSize: 40, mb: 1, color: cyber.accent.green }} />
                                    <Typography variant="h4" fontWeight="bold">24/7</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Real-Time Processing</Typography>
                                </StatsBox>
                                <StatsBox>
                                    <ArticleIcon sx={{ fontSize: 40, mb: 1, color: cyber.accent.cyan }} />
                                    <Typography variant="h4" fontWeight="bold">STIX 2.1</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Standard Export</Typography>
                                </StatsBox>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </HeroSection>

            {/* Core Features Section */}
            <Section id="features" sx={{ bgcolor: 'background.default' }}>
                <Container maxWidth="lg">
                    <Fade in timeout={1000}>
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" gutterBottom>
                                Core Capabilities
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                                Built for research and designed to explore AI-driven security operations
                            </Typography>
                        </Box>
                    </Fade>

                    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
                        {coreFeatures.map((feature, index) => (
                            <Fade in timeout={1200 + index * 100} key={index}>
                                <FeatureCard elevation={2}>
                                    <IconWrapper>
                                        {feature.icon}
                                    </IconWrapper>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        {feature.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                        {feature.description}
                                    </Typography>
                                </FeatureCard>
                            </Fade>
                        ))}
                    </Box>
                </Container>
            </Section>

            {/* Use Cases Section */}
            <Section sx={{ bgcolor: alpha(cyber.primary, 0.03) }}>
                <Container maxWidth="lg">
                    <Fade in timeout={1500}>
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <Typography variant="h3" fontWeight="bold" gutterBottom>
                                Who Benefits
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                Accelerating workflows across the security organization
                            </Typography>
                        </Box>
                    </Fade>

                    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
                        {useCases.map((useCase, index) => (
                            <Fade in timeout={1700 + index * 100} key={index}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(cyber.primary, 0.1)}`,
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateX(3px)',
                                            boxShadow: cyber.glow,
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: alpha(cyber.primary, 0.1),
                                                color: 'primary.main',
                                                flexShrink: 0
                                            }}
                                        >
                                            {useCase.icon}
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                {useCase.role}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {useCase.benefit}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Fade>
                        ))}
                    </Box>
                </Container>
            </Section>

            {/* Technical Details Section */}
            <Section>
                <Container maxWidth="lg">
                    <Fade in timeout={2000}>
                        <Paper
                            elevation={4}
                            sx={{
                                p: 5,
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.05)}, ${alpha(cyber.primaryLight, 0.05)})`,
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`
                            }}
                        >
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Technical Architecture
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                                TI Mindmap HUB leverages state-of-the-art Large Language Models (LLMs) for natural language understanding 
                                and information extraction. The platform processes raw threat intelligence articles through a multi-stage 
                                pipeline that includes:
                            </Typography>
                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, mt: 3 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>• Content Analysis</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Entity extraction, relationship mapping, and technical detail identification
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>• IOC Extraction</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Automated identification of IPs, domains, hashes, CVEs, and other indicators
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>• TTP Mapping</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Automatic alignment to MITRE ATT&CK framework with technique IDs
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>• Structured Output</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        STIX 2.1 bundles, JSON exports, and visual mindmap generation
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {/* AI Limitations Note */}
                            <Paper
                                elevation={0}
                                sx={{
                                    mt: 4,
                                    p: 2,
                                    bgcolor: alpha(cyber.primary, 0.06),
                                    border: `1px solid ${alpha(cyber.primary, 0.25)}`,
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Note:</strong> As with any AI system, outputs are experimental and require human verification. 
                                    Results may contain inaccuracies or omissions. See our <Link href="/research" color="primary">Research page</Link> for full details on limitations.
                                </Typography>
                            </Paper>
                        </Paper>
                    </Fade>
                </Container>
            </Section>

            {/* CTA Section */}
            <Section sx={{ bgcolor: alpha(cyber.primary, 0.03) }}>
                <Container maxWidth="md">
                    <Fade in timeout={2200}>
                        <Paper
                            elevation={8}
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${alpha(cyber.primary, 0.1)}, ${alpha(cyber.primaryLight, 0.1)})`,
                                border: `2px solid ${alpha(cyber.primary, 0.3)}`
                            }}
                        >
                            <Typography variant="h3" fontWeight="bold" gutterBottom>
                                Ready to Explore?
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                                Join the research preview and help shape the future of AI-powered threat intelligence
                            </Typography>
                            <CTAButton
                                size="large"
                                onClick={handleLogin}
                                startIcon={<LoginIcon />}
                            >
                                Access Research Preview
                            </CTAButton>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                                This is a research preview. Your feedback helps improve the platform and contributes to ongoing research.
                            </Typography>
                        </Paper>
                    </Fade>
                </Container>
            </Section>

            {/* Footer */}
            <Section sx={{ bgcolor: alpha('#000', 0.2), py: 4 }}>
                <Container maxWidth="lg">
                    <Divider sx={{ mb: 4 }} />
                    <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                        {/* About */}
                        <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                TI Mindmap HUB
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                An independent research project exploring Generative AI applications in automated threat intelligence processing.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                © 2026 TI Mindmap HUB. Research Preview.
                            </Typography>
                        </Box>

                        {/* Links */}
                        <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Legal & Research
                            </Typography>
                            <Stack spacing={1}>
                                <Link href="/research" color="text.secondary" underline="hover">
                                    Research Project
                                </Link>
                                <Link href="/research#publications" color="text.secondary" underline="hover">
                                    Publications
                                </Link>
                                <Link href="/terms" color="text.secondary" underline="hover">
                                    Terms of Service
                                </Link>
                                <Link href="/privacy" color="text.secondary" underline="hover">
                                    Privacy Policy
                                </Link>
                            </Stack>
                        </Box>

                        {/* Contact */}
                        <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Contact
                            </Typography>
                            <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                    <Link href="mailto:info@ti-mindmap-hub.com" color="primary.main" underline="hover">
                                        info@ti-mindmap-hub.com
                                    </Link>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <XIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                                    <Link href="https://x.com/ti_mindmap_hub" target="_blank" rel="noopener noreferrer" color="primary.main" underline="hover">
                                        @ti_mindmap_hub
                                    </Link>
                                </Stack>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                For research inquiries, feedback, or collaboration.
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* Independence Disclaimer */}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', opacity: 0.7 }}>
                        TI Mindmap HUB is an independent research project. It is not affiliated with, endorsed by, or representative of any organization. 
                        All views and outputs are experimental and do not represent any entity other than the project maintainer.
                    </Typography>
                </Container>
            </Section>
        </Box>
    );
};

export default LandingPage;